import { Model, type ModelProps } from '@/infra/model'
import { observable, computed } from 'mobx'
import { unixEpoch } from '@/utils/date-util'
import { Id } from '@/infra/id'

/**
 * Where a posting is in its lifecycle.
 */
export type WorkspacePostingStatus =
  'Draft' | 'Active' | 'Closed' | 'Cancelled' | 'InReview' | 'Unknown'

export type WorkspacePostingOpportunityProps =
  ModelProps<WorkspacePostingOpportunity>

/**
 * A workspace's posting for outsourcing work.
 */
export class WorkspacePostingOpportunity extends Model<WorkspacePostingOpportunity> {
  id = Id.none
  @observable accessor workspaceId = Id.none
  @observable accessor dateCreated: Date = unixEpoch()
  @observable accessor dateModified: Date = unixEpoch()
  @observable accessor location = ''
  @observable accessor primarySoftware = ''
  @observable accessor supplementalSoftware = ''
  @observable accessor backupFileNeeded = false
  @observable accessor specialties: string[] = []
  @observable accessor jobTypes: WorkspacePostingJobType[] = []
  @observable accessor capacity = 0
  @observable accessor description = ''
  @observable accessor maxAllowedWorkspaces = 0
  @observable accessor status: WorkspacePostingStatus = 'Unknown'
  @observable accessor isApplicant = false

  /**
   * The software requirements, as display lines.
   */
  @computed
  get requirements() {
    const req = []
    if (this.primarySoftware) {
      req.push(
        this.primarySoftware +
          `${this.backupFileNeeded ? ' with backup file needed' : ''}`,
      )
    }
    if (this.supplementalSoftware) {
      req.push(this.supplementalSoftware)
    }
    return req
  }

  /**
   * Whether the posting is active.
   */
  @computed
  get isActive() {
    return this.status === 'Active'
  }

  /**
   * Whether the posting is in review.
   */
  @computed
  get isInReview() {
    return this.status === 'InReview'
  }
}

/**
 * The kind of work a posting is for.
 */
export type WorkspacePostingJobType = 'Prep' | 'Review' | 'Bookkeeping'
